// HotjarLoader.jsx
import { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import usePreviousValue from '../hook/usePreviousValue.js';
import { initHotjar } from '../resource/hotjar.js';

const DELAY_LOADING_MSEC = 10000; // TODO: remote config
const listenEvents = ['mousemove', 'scroll', 'click'];

export const HotjarLoader = ({
  hotjarSiteId = '',
  hotjarVersion = '',
  enableHotjar = false,
}) => {
  const prevEnableHotjar = usePreviousValue(enableHotjar);
  const idleCallback = useRef();
  const timeout = useRef();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clearSideEffects = () => {
    listenEvents.forEach(event => {
      document.removeEventListener(event, loadHotjar);
    });
    cancelIdleCallback(idleCallback.current);
    clearTimeout(timeout.current);
  };

  const loadHotjar = useCallback(async () => {
    clearSideEffects();
    await initHotjar({ siteId: hotjarSiteId, version: hotjarVersion });
  }, [clearSideEffects, hotjarSiteId, hotjarVersion]);

  const initTimeout = useCallback(() => {
    timeout.current = setTimeout(loadHotjar, DELAY_LOADING_MSEC);
  }, [loadHotjar]);

  const lazyLoadHotjar = useCallback(() => {
    idleCallback.current = requestIdleCallback(initTimeout);
    listenEvents.forEach(event => {
      document.addEventListener(event, loadHotjar);
    });
  }, [loadHotjar, initTimeout]);

  // enable hotjar when enableHotjar default is true
  useEffect(() => {
    if (enableHotjar) {
      lazyLoadHotjar();
    }

    return clearSideEffects;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // enable hotjar when remote config changed
  useEffect(() => {
    const shouldLoadHotjar =
      enableHotjar &&
      prevEnableHotjar !== undefined &&
      prevEnableHotjar !== enableHotjar;

    if (shouldLoadHotjar) {
      lazyLoadHotjar();
    }
  }, [enableHotjar, prevEnableHotjar, lazyLoadHotjar]);

  return null;
};

HotjarLoader.propTypes = {
  hotjarSiteId: PropTypes.string,
  hotjarVersion: PropTypes.string,
  enableHotjar: PropTypes.bool,
};

export default HotjarLoader;
